import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { createSubanchor, updateSubanchor } from '../services/Wallet/subAnchorService';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatMobile = (v) => (v == null || v === '' ? '' : String(v));

const AddSubanchorModal = ({ isOpen, onClose, anchorId, onSuccess, editData }) => {
  const toast = useToast();
  const isEdit = !!editData;
  const [formData, setFormData] = useState({ code: '', mobile: '', email: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setFormData({
          code: editData.code ?? '',
          mobile: formatMobile(editData.mobile),
          email: editData.email ?? '',
        });
      } else {
        setFormData({ code: '', mobile: '', email: '' });
      }
      setErrors({});
    }
  }, [isOpen, editData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    const code = formData.code?.trim() ?? '';
    if (!isEdit && !code) {
      newErrors.code = 'Subanchor code is required';
    }
    if (code.length > 50) {
      newErrors.code = 'Code must be at most 50 characters';
    }
    if (formData.mobile?.trim()) {
      const digits = formData.mobile.replace(/\D/g, '');
      if (digits.length !== 10) {
        newErrors.mobile = 'Mobile must be 10 digits';
      }
    }
    if (formData.email?.trim() && !EMAIL_REGEX.test(formData.email.trim())) {
      newErrors.email = 'Enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || !anchorId) return;
    if (isEdit && !editData?.walletId) return;

    setSubmitting(true);
    try {
      const payload = {};
      if (formData.code?.trim()) payload.code = formData.code.trim();
      if (formData.mobile?.trim()) {
        const digits = formData.mobile.replace(/\D/g, '');
        if (digits.length === 10) payload.mobile = parseInt(digits, 10);
      }
      if (formData.email?.trim()) payload.email = formData.email.trim();
      if (!isEdit && !payload.code) {
        setErrors({ code: 'Subanchor code is required' });
        setSubmitting(false);
        return;
      }
      if (isEdit && Object.keys(payload).length === 0) {
        toast({ title: 'No changes', description: 'Update at least one field.', status: 'info', duration: 3000, isClosable: true });
        setSubmitting(false);
        return;
      }

      const data = isEdit
        ? await updateSubanchor(anchorId, editData.walletId, payload)
        : await createSubanchor(anchorId, payload);

      if (data?.metadata?.api_status !== false) {
        const successMsg = typeof data?.msg === 'string' ? data.msg : (isEdit ? 'Subanchor updated.' : 'Subanchor created successfully.');
        toast({
          title: 'Success',
          description: successMsg,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onSuccess?.();
        onClose?.();
      } else {
        const errorMsg = typeof data?.msg === 'string' ? data.msg : (isEdit ? 'Failed to update subanchor.' : 'Failed to create subanchor.');
        toast({
          title: 'Error',
          description: errorMsg,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (err) {
      const fieldErrors = {};
      if (err.data?.msg && Array.isArray(err.data.msg)) {
        err.data.msg.forEach((item) => {
          if (item?.key) fieldErrors[item.key] = item.value || item;
        });
        setErrors(fieldErrors);
      }
      const message = typeof err.message === 'string' ? err.message : (isEdit ? 'Failed to update subanchor.' : 'Failed to create subanchor.');
      toast({ title: 'Error', description: message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed top-0 left-0 w-screen h-screen bg-black/80 z-[9999] m-0 p-0 flex items-center justify-center">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl m-6">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Subanchor' : 'Add Subanchor'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <FormControl isInvalid={!!errors.code} isRequired={!isEdit}>
            <FormLabel className="text-sm font-medium text-gray-700">
              Subanchor code (DSA code) {!isEdit ? '*' : ''}
            </FormLabel>
            <Input
              value={formData.code}
              onChange={(e) => handleChange('code', e.target.value)}
              placeholder="e.g. DSA001, AGENT_A"
              maxLength={51}
              className="border-gray-300"
            />
            {errors.code && (
              <p className="text-xs text-red-500 mt-1">{errors.code}</p>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.mobile}>
            <FormLabel className="text-sm font-medium text-gray-700">
              Mobile
            </FormLabel>
            <Input
              type="tel"
              value={formData.mobile}
              onChange={(e) => handleChange('mobile', e.target.value)}
              placeholder="10-digit mobile number"
              className="border-gray-300"
            />
            {errors.mobile && (
              <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.email}>
            <FormLabel className="text-sm font-medium text-gray-700">
              Email
            </FormLabel>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="dsa@example.com"
              className="border-gray-300"
            />
            {errors.email && (
              <p className="text-xs text-red-500 mt-1">{errors.email}</p>
            )}
          </FormControl>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} size="sm">
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              size="sm"
              isLoading={submitting}
              spinner={<Spinner size="sm" />}
            >
              {isEdit ? 'Update' : 'Submit'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddSubanchorModal;
